/**
 * D4F ENGRAVING SHOWCASE
 * - Before/After Image Slider
 * - Rotating Example Configurations
 * - Interactive Engraving Preview
 */
(function () {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;

    const sectionId = section.dataset.sectionId;

    // Initialize all components
    initBeforeAfterSlider();
    initExampleRotator();
    initEngravingAreas();
    initTabSwitcher();
    setupEventListeners();
    initObservers();
    initLazyLoad();

    // Performance monitoring
    if (window.performance?.mark) {
      window.performance.mark('d4f-engraving-interactive');
    }

    console.debug('[D4F Engraving Showcase] Initialized successfully');
  }

  /**
   * BEFORE/AFTER IMAGE SLIDER
   */
  function initBeforeAfterSlider() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const sliders = section.querySelectorAll('.d4f-engraving__comparison');
    
    sliders.forEach(slider => {
      const handle = slider.querySelector('.d4f-engraving__comparison-handle');
      const beforeElement = slider.querySelector('.d4f-engraving__comparison-before');
      const hint = section.querySelector('.d4f-engraving__comparison-hint');
      
      if (!handle || !beforeElement) {
        console.warn('[D4F Engraving] Handle or before element not found');
        return;
      }

      let isDragging = false;
      let containerRect = null;

      // Initialize
      updateRect();
      const startPosition = 50;
      updatePosition(startPosition);

      // Update rect on resize
      window.addEventListener('resize', updateRect);

      function updateRect() {
        containerRect = slider.getBoundingClientRect();
      }

      // Mouse events
      handle.addEventListener('mousedown', startDrag);
      document.addEventListener('mousemove', drag);
      document.addEventListener('mouseup', stopDrag);

      // Touch events
      handle.addEventListener('touchstart', startDrag, { passive: false });
      document.addEventListener('touchmove', drag, { passive: false });
      document.addEventListener('touchend', stopDrag);

      // Click/Tap anywhere to jump
      slider.addEventListener('click', (e) => {
        if (e.target.closest('.d4f-engraving__comparison-handle')) return;
        jumpToPosition(e);
      });

      // Keyboard accessibility
      handle.addEventListener('keydown', (e) => {
        const currentPercent = parseFloat(handle.style.left) || 50;
        let newPercent = currentPercent;

        if (e.key === 'ArrowLeft') {
          newPercent = Math.max(0, currentPercent - 2);
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          newPercent = Math.min(100, currentPercent + 2);
          e.preventDefault();
        }

        if (newPercent !== currentPercent) {
          updatePosition(newPercent);
        }
      });

      function startDrag(e) {
        isDragging = true;
        slider.classList.add('is-dragging');
        handle.style.cursor = 'grabbing';
        updateRect();

        // Hide hint
        if (hint) {
          hint.style.opacity = '0';
        }

        e.preventDefault();
      }

      function drag(e) {
        if (!isDragging) return;

        const x = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
        const percent = calculatePercent(x);
        updatePosition(percent);

        e.preventDefault();
      }

      function stopDrag() {
        if (!isDragging) return;

        isDragging = false;
        slider.classList.remove('is-dragging');
        handle.style.cursor = 'grab';
      }

      function jumpToPosition(e) {
        const x = e.type.includes('touch') ? e.changedTouches[0].clientX : e.clientX;
        const percent = calculatePercent(x);
        updatePosition(percent);

        // Hide hint
        if (hint) {
          hint.style.opacity = '0';
        }
      }

      function calculatePercent(clientX) {
        const containerLeft = containerRect.left;
        const containerWidth = containerRect.width;
        const relativeX = clientX - containerLeft;
        const percent = (relativeX / containerWidth) * 100;

        // Clamp between 0 and 100
        return Math.max(0, Math.min(100, percent));
      }

      function updatePosition(percent) {
        // Update handle position
        handle.style.left = `${percent}%`;

        // Update before image width
        beforeElement.style.width = `${percent}%`;
      }

      console.debug('[D4F Engraving] Before/After slider initialized');
    });
  }

  /**
   * EXAMPLE CONFIGURATIONS ROTATOR
   */
  function initExampleRotator() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const rotatorContainers = section.querySelectorAll('.d4f-engraving__examples-rotator');
    
    rotatorContainers.forEach(container => {
      const examples = container.querySelectorAll('.d4f-engraving__example');
      if (examples.length <= 1) return;

      let currentIndex = 0;
      const intervalTime = 4000; // 4 seconds

      // Show first example
      examples[0].classList.add('active');

      // Auto-rotate
      setInterval(() => {
        // Hide current
        examples[currentIndex].classList.remove('active');
        
        // Show next
        currentIndex = (currentIndex + 1) % examples.length;
        examples[currentIndex].classList.add('active');
      }, intervalTime);
    });
  }

  /**
   * ENGRAVING AREAS INTERACTIVE
   */
  function initEngravingAreas() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const areaCards = section.querySelectorAll('.d4f-engraving__area-card');
    
    areaCards.forEach((card, index) => {
      card.addEventListener('mouseenter', function() {
        // Highlight corresponding area in preview
        const previewAreas = section.querySelectorAll('.d4f-engraving__preview-area');
        if (previewAreas[index]) {
          previewAreas[index].classList.add('highlighted');
        }
      });

      card.addEventListener('mouseleave', function() {
        const previewAreas = section.querySelectorAll('.d4f-engraving__preview-area');
        if (previewAreas[index]) {
          previewAreas[index].classList.remove('highlighted');
        }
      });
    });
  }

  /**
   * TAB SWITCHER
   */
  function initTabSwitcher() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const tabButtons = section.querySelectorAll('.d4f-engraving__tab-btn');
    const tabPanels = section.querySelectorAll('.d4f-engraving__tab-panel');

    tabButtons.forEach(button => {
      button.addEventListener('click', function() {
        const targetTab = this.dataset.tab;

        // Remove active from all
        tabButtons.forEach(btn => btn.classList.remove('active'));
        tabPanels.forEach(panel => panel.classList.remove('active'));

        // Add active to clicked
        this.classList.add('active');
        const targetPanel = section.querySelector(`[data-tab-panel="${targetTab}"]`);
        if (targetPanel) {
          targetPanel.classList.add('active');
        }
      });
    });
  }

  /**
   * EVENT LISTENERS
   */
  function setupEventListeners() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    // Live Preview Button
    const previewButtons = section.querySelectorAll('.d4f-engraving__preview-btn');
    previewButtons.forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        openLivePreview();
      });
    });

    // Start Customization CTA
    const customizeCTAs = section.querySelectorAll('.d4f-engraving__customize-cta');
    customizeCTAs.forEach(cta => {
      cta.addEventListener('click', function(e) {
        // Track conversion event
        if (window.dataLayer) {
          window.dataLayer.push({
            'event': 'customize_click',
            'section': 'engraving_showcase'
          });
        }
      });
    });
  }

  /**
   * OPEN LIVE PREVIEW MODAL
   */
  function openLivePreview() {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'd4f-engraving__modal';
    modal.innerHTML = `
      <div class="d4f-engraving__modal-overlay"></div>
      <div class="d4f-engraving__modal-content">
        <button class="d4f-engraving__modal-close">×</button>
        <div class="d4f-engraving__modal-header">
          <h3>Live Vorschau</h3>
          <p>Erlebe die Personalisierung in Echtzeit</p>
        </div>
        <div class="d4f-engraving__modal-body">
          <div class="d4f-engraving__live-demo">
            <div class="d4f-engraving__demo-placeholder">
              <span class="d4f-engraving__demo-icon">✨</span>
              <p>Hier wird der Teeinblue Personalizer eingebettet</p>
              <small>Produktseite öffnen für Live-Demo</small>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';

    // Close handlers
    const closeBtn = modal.querySelector('.d4f-engraving__modal-close');
    const overlay = modal.querySelector('.d4f-engraving__modal-overlay');

    closeBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);

    // Animate in
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);

    function closeModal() {
      modal.classList.remove('active');
      setTimeout(() => {
        modal.remove();
        document.body.style.overflow = '';
      }, 300);
    }
  }

  /**
   * INTERSECTION OBSERVER for animations
   */
  function initObservers() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe elements
    const animatedElements = section.querySelectorAll(
      '.d4f-engraving__feature-card, .d4f-engraving__area-card, .d4f-engraving__example'
    );
    
    animatedElements.forEach(el => observer.observe(el));
  }

  /**
   * LAZY LOAD IMAGES
   */
  function initLazyLoad() {
    const section = document.querySelector('.d4f-engraving-showcase');
    if (!section) return;
    
    const lazyImages = section.querySelectorAll('img[loading="lazy"]');
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src || img.src;
            imageObserver.unobserve(img);
          }
        });
      });

      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }
})();